import React, { useState, FormEvent } from 'react';

import { Checkbox } from 'primereact/checkbox';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faArrowsLeftRightToLine,
  faSquarePlus,
  faSnowflake,
  faCircleExclamation,
  faUpRightAndDownLeftFromCenter,
  faCalculator,
} from '@fortawesome/free-solid-svg-icons';

import { useMutation } from '@tanstack/react-query';
import { useToastNotification } from '@/hooks';
import { RadioButton } from 'primereact/radiobutton';
import { InputNumberValueChangeEvent } from 'primereact/inputnumber';
import { InputNumberField } from '@/components';
import { Button } from 'primereact/button';
import classNames from 'classnames';
import { generateExport } from './generateExport';
import styles from './CostSimulator.module.scss';

export interface CostSimulatorFormState {
  cntDiasAntesCierreStacking?: number;
  cntDiasAntesComOP?: number;
  cntDiasFueraVentana?: number;
  cobraEarlyArrival: boolean;
  cobraLateArrival: boolean;
  esIMO: boolean;
  esOOG: boolean;
  esReefer: boolean;
  tamanioContenedor: number;
  esOperado: boolean;
  ventanaIngreso?: boolean;
}

interface CostSimulatorFormErrors {
  cntDiasAntesCierreStacking: string;
  cntDiasAntesComOP: string;
  cntDiasFueraVentana: string;
  cobraEarlyArrival: string;
  cobraLateArrival: string;
  esIMO: string;
  esOOG: string;
  esReefer: string;
  tamanioContenedor: string;
  esOperado: string;
}

interface CostSimulatorExportProps {
  setExportData: (
    results: null,
    formDataExport: CostSimulatorFormState,
    total: number
  ) => void;
  setIsExportSliderOpen: (value: boolean) => void;
}

export const CostSimulatorExport: React.FC<CostSimulatorExportProps> = ({
  setExportData,
  setIsExportSliderOpen,
}) => {
  const { t } = useTranslation('costSimulatorForm');
  const { showToast } = useToastNotification();
  const [isLoading, setIsLoading] = useState(false);

  const [ventanaIngreso, setVentanaIngreso] = useState(true);

  const [formData, setFormData] = useState<CostSimulatorFormState>({
    cobraEarlyArrival: false,
    cobraLateArrival: false,
    esIMO: false,
    esOOG: false,
    esReefer: false,
    tamanioContenedor: 20,
    esOperado: true,
    cntDiasAntesCierreStacking: 0,
    cntDiasAntesComOP: 0,
    cntDiasFueraVentana: 0,
  });

  const [formErrors, setFormErrors] = useState<CostSimulatorFormErrors>({
    cntDiasAntesCierreStacking: '',
    cntDiasAntesComOP: '',
    cntDiasFueraVentana: '',
    cobraEarlyArrival: '',
    cobraLateArrival: '',
    esIMO: '',
    esOOG: '',
    esReefer: '',
    tamanioContenedor: '',
    esOperado: '',
  });

  const validateForm = () => {
    const errors: CostSimulatorFormErrors = { ...formErrors };

    return Object.values(errors).every((x) => x === '');
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement> | InputNumberValueChangeEvent,
    name: keyof CostSimulatorFormState
  ) => {
    const { value, checked, type } = e.target;
    const updatedValue = type === 'checkbox' ? checked : value;

    setFormData((prevState) => ({
      ...prevState,
      [name]: updatedValue,
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };
  const handleCheckboxChange = (
    name: keyof Omit<CostSimulatorFormState, 'cntDiasEarlyArrival' | 'tamano'>
  ) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: !prevState[name],
    }));
  };

  const generateExportMutation = useMutation({
    mutationFn: generateExport,
    onMutate: () => {
      setIsLoading(true);
      setIsExportSliderOpen(false);
    },
    onSuccess: (response) => {
      setExportData(
        response?.data?.value?.detalle,
        { ...formData, ventanaIngreso },
        response?.data?.value?.total
      );
      setIsExportSliderOpen(true);
      setIsLoading(false);
    },
    onError: (error) => {
      setIsLoading(false);

      if (error.toString().trim() !== 'Error') {
        showToast({ severity: 'error', detail: `${error}` });
      } else {
        showToast({
          severity: 'error',
          detail: t('unexpectedError'),
        });
      }
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    validateForm();
    if (validateForm()) {
      generateExportMutation.mutate(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.cfeForm}>
      <div className={classNames(styles.formSection, styles.entry)}>
        <label htmlFor="daysInTerminal" className={styles.formSectionTitle}>
          <FontAwesomeIcon icon={faCalendarAlt} className={styles.icon} />
          {t('income')}:
        </label>
        <div className={styles.checkboxInputField}>
          <RadioButton
            inputId="window"
            name="window"
            onChange={() => {
              setVentanaIngreso(true);
              setFormData({
                ...formData,
                cntDiasFueraVentana: 0,
                cobraLateArrival: false,
                cobraEarlyArrival: false,
              });
            }}
            checked={ventanaIngreso === true}
          />
          <label htmlFor="window">{t('window')}</label>
        </div>
        <div className={styles.checkboxInputField}>
          <RadioButton
            inputId="cobraEarlyArrival"
            name="cobraEarlyArrival"
            onChange={() => {
              setVentanaIngreso(false);
              setFormData({
                ...formData,
                cobraEarlyArrival: true,
                cobraLateArrival: false,
              });
            }}
            checked={formData.cobraEarlyArrival === true}
          />
          <label htmlFor="cobraEarlyArrival">{t('earEarlyArrival')}</label>
        </div>
        {formData.cobraEarlyArrival && (
          <div
            className={classNames(
              styles.maxWidthInput,
              styles.earlyArrivalInput
            )}
          >
            <InputNumberField
              value={formData.cntDiasFueraVentana ?? null}
              onChange={(e) => handleInputChange(e, 'cntDiasFueraVentana')}
              id="cntDiasFueraVentana"
              label={t('daysBeforeStackingOpening')}
              name="cntDiasFueraVentana"
              allowOnlyWholeNumbers
              minNumber={0}
            />
          </div>
        )}
        <div className={styles.checkboxInputField}>
          <RadioButton
            inputId="cobraLateArrival"
            name="cobraLateArrival"
            onChange={() => {
              setVentanaIngreso(false);
              setFormData({
                ...formData,
                cntDiasFueraVentana: 0,
                cobraLateArrival: true,
                cobraEarlyArrival: false,
              });
            }}
            checked={formData.cobraLateArrival === true}
          />
          <label htmlFor="cobraLateArrival">{t('larLateArrival')}</label>
        </div>
      </div>
      <div className={styles.formSection}>
        <label className={styles.formSectionTitle}>
          <FontAwesomeIcon
            icon={faArrowsLeftRightToLine}
            className={styles.icon}
          />
          {t('sizeFeet')}:
        </label>
        <div className={styles.radioButtonsContainer}>
          <div className={styles.checkboxInputField}>
            <RadioButton
              inputId="size20"
              name="size"
              value={20}
              onChange={(e) => handleInputChange(e, 'tamanioContenedor')}
              checked={formData.tamanioContenedor === 20}
            />
            <label htmlFor="size20">{t('20Feet')}</label>
          </div>
          <div className={styles.checkboxInputField}>
            <RadioButton
              inputId="size40"
              name="size"
              value={40}
              onChange={(e) => handleInputChange(e, 'tamanioContenedor')}
              checked={formData.tamanioContenedor === 40}
            />
            <label htmlFor="size40">{t('40Feet')}</label>
          </div>
        </div>
      </div>
      <div
        className={classNames(
          styles.formSection,
          styles.additionalCharacteristics
        )}
      >
        <label className={styles.formSectionTitle}>
          <FontAwesomeIcon icon={faSquarePlus} className={styles.icon} />
          {t('additionalFeatures')}
        </label>
        <div className={styles.checkboxInputField}>
          <Checkbox
            inputId="esIMO"
            checked={formData.esIMO}
            onChange={() => handleCheckboxChange('esIMO')}
          />
          <label htmlFor="esIMO">
            <FontAwesomeIcon
              icon={faCircleExclamation}
              className={styles.icon}
            />
            {t('imdg')}
          </label>
        </div>

        {(formData.esIMO || formData.esOOG) && (
          <div className={styles.maxWidthInputComOps}>
            <InputNumberField
              value={formData?.cntDiasAntesComOP ?? null}
              onChange={(e) => handleInputChange(e, 'cntDiasAntesComOP')}
              id="cntDiasAntesComOP"
              label={t('daysBeforeCOMOOPS')}
              name="cntDiasAntesComOP"
              allowOnlyWholeNumbers
            />
          </div>
        )}

        <div className={styles.checkboxInputField}>
          <Checkbox
            inputId="esOOG"
            checked={formData.esOOG}
            onChange={() => handleCheckboxChange('esOOG')}
          />
          <label htmlFor="esOOG">
            <FontAwesomeIcon
              icon={faUpRightAndDownLeftFromCenter}
              className={styles.icon}
            />
            {t('oog')}
          </label>
        </div>

        <div className={styles.checkboxInputField}>
          <Checkbox
            inputId="esReeferOperativo"
            checked={formData.esReefer}
            onChange={() => handleCheckboxChange('esReefer')}
          />
          <label htmlFor="esReeferOperativo">
            <FontAwesomeIcon icon={faSnowflake} className={styles.icon} />
            {t('operativeReefer')}
          </label>
        </div>
        {formData.esReefer && (
          <div className={styles.maxWidthInputComOps}>
            <InputNumberField
              value={formData.cntDiasAntesCierreStacking ?? null}
              onChange={(e) =>
                handleInputChange(e, 'cntDiasAntesCierreStacking')
              }
              id="cntDiasAntesCierreStacking"
              label={t('daysBeforeStackingClosure')}
              name="cntDiasAntesCierreStacking"
              allowOnlyWholeNumbers
            />
          </div>
        )}
      </div>
      <div className={styles.buttonContainer}>
        <Button
          type="submit"
          label=""
          severity="success"
          className={styles.calcButton}
          loading={isLoading}
        >
          <FontAwesomeIcon icon={faCalculator} className={styles.icon} />
          {t('calculate')}
        </Button>
      </div>
      <span className={styles.disclaimer}>{t('calculationDisclaimer')}</span>
    </form>
  );
};
